import React, { useMemo, useState } from "react";
import { calculateRetention } from "../utils/retentionCalculations";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Collapse,
} from "@mui/material";
import TroubleshootIcon from "@mui/icons-material/Troubleshoot";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

const RetentionMonitor = ({ rawData, settings }) => {
  const [isTableOpen, setIsTableOpen] = useState(false);
  const { production, expiries } = rawData;

  // --- NEW RETENTION ENGINE ---
  const { enrichedExpiries, stats } = useMemo(() => {
    return calculateRetention(production, expiries, settings);
  }, [production, expiries, settings]);

  const handleExportCSV = () => {
    const headers = [
      "Targa Originale",
      "Targa Normalizzata",
      "Cliente",
      "Collaboratore",
      "Scadenza",
      "Causale",
      "Eleggibile",
      "Motivo Esclusione",
      "Stato Retention",
      "Dettaglio Match",
      "Data Match",
      "Distanza Giorni",
    ];

    const rows = enrichedExpiries.map((item) => [
      item.riferimento,
      item.normalizedRef,
      item.cliente || "-",
      item.collaboratore,
      item.scadenzaCopertura,
      item.causale || "-",
      item.isEligible ? "SI" : "NO",
      item.exclusionReason || "-",
      item.retentionStatus,
      item.retentionDetails?.matchId || "-",
      item.retentionDetails?.matchDate || "-",
      item.retentionDetails?.matchDistance?.toString() || "-",
    ]);

    const csvContent = [
      headers.join(";"),
      ...rows.map((r) => r.join(";")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `retention_audit_${new Date().toISOString().split("T")[0]}.csv`,
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Diagnostics Panel */}
      <Paper
        sx={{
          borderRadius: 4,
          p: 3,
          color: "common.white",
          boxShadow: 4,
          border: 1,
          borderColor: "secondary.light",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: "primary.main",
                display: "flex",
                alignItems: "center",
              }}
            >
              <TroubleshootIcon sx={{ mr: 1, fontSize: "1.25rem" }} />
              Retention Engine 2.0
            </Typography>
            <Typography
              sx={{
                fontSize: "0.625rem",
                color: "grey.400",
                fontWeight: "medium",
                mt: 0.5,
              }}
            >
              Calcolo rigoroso: Window -30/+15gg, Deduplica, Filtri Avanzati
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: "0.5625rem",
                  fontWeight: "bold",
                  color: "grey.500",
                  textTransform: "uppercase",
                }}
              >
                Scadenze Totali
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 900,
                  color: "common.white",
                }}
              >
                {expiries.length}
              </Typography>
            </Box>
            <Box sx={{ width: "1px" }}></Box>
            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontSize: "0.5625rem",
                  fontWeight: "bold",
                  color: "success.main",
                  textTransform: "uppercase",
                }}
              >
                Eleggibili
              </Typography>
              <Typography
                sx={{
                  fontSize: "1.25rem",
                  fontWeight: 900,
                  color: "success.light",
                }}
              >
                {stats.totalEligible}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Grid
          container
          spacing={2}
          sx={{
            p: 2,
            borderRadius: 3,
            border: 1,
            borderColor: "rgba(51, 65, 85, 0.5)",
          }}
        >
          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: "rgba(16, 185, 129, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "success.main",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                }}
              >
                {stats.retentionRate.toFixed(1)}%
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.5625rem",
                    fontWeight: 900,
                    color: "grey.400",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Retention Ufficiale
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "grey.200",
                  }}
                >
                  {stats.renewed} su {stats.totalEligible}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: "rgba(244, 63, 94, 0.2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "error.main",
                  fontWeight: "bold",
                  fontSize: "0.75rem",
                }}
              >
                {stats.notRenewed}
              </Box>
              <Box>
                <Typography
                  sx={{
                    fontSize: "0.5625rem",
                    fontWeight: 900,
                    color: "grey.400",
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Non Rinnovate
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: "bold",
                    color: "grey.200",
                  }}
                >
                  Perse o Non Trovate
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "secondary.light",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.5rem",
                      color: "grey.500",
                      textTransform: "uppercase",
                    }}
                  >
                    Fuori Finestra
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      color: "warning.main",
                    }}
                  >
                    {stats.outOfWindow}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "secondary.light",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.5rem",
                      color: "grey.500",
                      textTransform: "uppercase",
                    }}
                  >
                    Escluse * / PRP
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      color: "grey.300",
                    }}
                  >
                    {stats.excludedAsterisk + stats.excludedPrp}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={4}>
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    border: 1,
                    borderColor: "secondary.light",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.5rem",
                      color: "grey.500",
                      textTransform: "uppercase",
                    }}
                  >
                    Duplicati
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.875rem",
                      fontWeight: "bold",
                      color: "grey.300",
                    }}
                  >
                    {stats.duplicates}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Main Table */}
      <Paper
        sx={{
          borderRadius: 4,
          overflow: "hidden",
          border: 1,
          borderColor: "grey.100",
          boxShadow: 1,
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: 1,
            borderColor: "grey.100",
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            justifyContent: "space-between",
            alignItems: { lg: "center" },
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconButton
              onClick={() => setIsTableOpen(!isTableOpen)}
              size="small"
              sx={{ color: "text.primary" }}
            >
              {isTableOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", color: "text.primary" }}
              >
                Audit Scadenze
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", mt: 0.5, display: "block" }}
              >
                Lista completa scadenze con esito matching e motivi esclusione.
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 3,
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={handleExportCSV}
              startIcon={<DownloadIcon sx={{ color: "info.main" }} />}
              sx={{
                fontSize: "0.625rem",
                fontWeight: 900,
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                borderColor: "grey.200",
                color: "text.secondary",
                borderRadius: 3,
                py: 1,
                "&:hover": { bgcolor: "background.paper" },
              }}
            >
              Esporta Audit CSV
            </Button>
          </Box>
        </Box>
        <Collapse in={isTableOpen} timeout="auto" unmountOnExit>
          <TableContainer>
          <Table sx={{ minWidth: 650 }} aria-label="retention audit table">
            <TableHead>
              <TableRow
                sx={{
                  borderBottom: 1,
                  borderColor: "grey.100",
                }}
              >
                <TableCell
                  sx={{
                    px: 3,
                    py: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "text.disabled",
                    textTransform: "uppercase",
                  }}
                >
                  Targa
                </TableCell>
                <TableCell
                  sx={{
                    px: 3,
                    py: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "text.disabled",
                    textTransform: "uppercase",
                  }}
                >
                  Cliente
                </TableCell>
                <TableCell
                  sx={{
                    px: 3,
                    py: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "text.disabled",
                    textTransform: "uppercase",
                  }}
                >
                  Scadenza
                </TableCell>
                <TableCell
                  sx={{
                    px: 3,
                    py: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "text.disabled",
                    textTransform: "uppercase",
                  }}
                >
                  Eleggibile
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    px: 3,
                    py: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "text.disabled",
                    textTransform: "uppercase",
                  }}
                >
                  Stato
                </TableCell>
                <TableCell
                  sx={{
                    px: 3,
                    py: 2,
                    fontSize: "0.625rem",
                    fontWeight: 900,
                    color: "text.disabled",
                    textTransform: "uppercase",
                  }}
                >
                  Dettagli
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {enrichedExpiries.slice(0, 100).map((item, idx) => (
                <TableRow
                  key={idx}
                  hover
                  sx={{
                    bgcolor: !item.isEligible ? "" : "inherit",
                    opacity: !item.isEligible ? 0.5 : 1,
                    "&:last-child td, &:last-child th": { border: 0 },
                  }}
                >
                  <TableCell sx={{ px: 3, py: 1.5 }}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          color: "text.primary",
                          fontFamily: "monospace",
                          fontSize: "0.75rem",
                        }}
                      >
                        {item.normalizedRef}
                      </Typography>
                      <Typography
                        sx={{ fontSize: "0.5625rem", color: "text.disabled" }}
                      >
                        {item.riferimento}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ px: 3, py: 1.5 }}>
                    <Typography
                      sx={{
                        fontWeight: "bold",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        maxWidth: 120,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {item.cliente || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ px: 3, py: 1.5 }}>
                    <Typography
                      sx={{
                        color: "text.secondary",
                        fontWeight: "bold",
                        fontSize: "0.75rem",
                      }}
                    >
                      {item.scadenzaCopertura}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ px: 3, py: 1.5 }}>
                    {item.isEligible ? (
                      <Typography
                        sx={{
                          color: "success.main",
                          fontWeight: "bold",
                          fontSize: "0.625rem",
                        }}
                      >
                        SI
                      </Typography>
                    ) : (
                      <Typography
                        sx={{
                          color: "error.light",
                          fontWeight: "bold",
                          fontSize: "0.625rem",
                        }}
                      >
                        {item.exclusionReason}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="center" sx={{ px: 3, py: 1.5 }}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Chip
                        label={item.retentionStatus.replace("_", " ")}
                        size="small"
                        sx={{
                          fontSize: "0.5625rem",
                          fontWeight: 900,
                          textTransform: "uppercase",
                          letterSpacing: "-0.05em",
                          bgcolor:
                            item.retentionStatus === "RENEWED"
                              ? "success.100"
                              : item.retentionStatus === "NOT_RENEWED"
                              ? "error.100"
                              : item.retentionStatus === "OUT_OF_WINDOW"
                              ? "warning.100"
                              : "grey.100",
                          color:
                            item.retentionStatus === "RENEWED"
                              ? "success.700"
                              : item.retentionStatus === "NOT_RENEWED"
                              ? "error.700"
                              : item.retentionStatus === "OUT_OF_WINDOW"
                              ? "warning.700"
                              : "grey.500",
                          borderColor:
                            item.retentionStatus === "RENEWED"
                              ? "success.200"
                              : item.retentionStatus === "NOT_RENEWED"
                              ? "error.200"
                              : item.retentionStatus === "OUT_OF_WINDOW"
                              ? "warning.200"
                              : "grey.200",
                          border: 1,
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell sx={{ px: 3, py: 1.5 }}>
                    {item.retentionDetails ? (
                      <Box
                        sx={{ fontSize: "0.625rem", color: "text.secondary" }}
                      >
                        <Typography variant="inherit">
                          Match: {item.retentionDetails.matchDate}
                        </Typography>
                        <Typography variant="inherit">
                          Dist: {item.retentionDetails.matchDistance}gg
                        </Typography>
                      </Box>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {enrichedExpiries.length > 100 && (
            <Box
              sx={{
                p: 2,
                textAlign: "center",
                fontSize: "0.75rem",
                color: "text.disabled",
                fontStyle: "italic",
              }}
            >
              Mostrate prime 100 righe di {enrichedExpiries.length}. Esporta CSV
              per lista completa.
            </Box>
          )}
        </TableContainer>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default RetentionMonitor;
